import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { IconChevronDown, IconChevronUp, IconClose, IconSearch } from './icons'
import { useI18n } from '../i18n'
import { toast } from '../store/toast'
import { authHeaders } from '../api'

/** Le label de section. `accent` le passe en vert pour ce qui demande une action. */
export function Eyebrow({ children, accent }: { children: ReactNode; accent?: boolean }) {
  return <div className={accent ? 'eyebrow eyebrow--accent' : 'eyebrow'}>{children}</div>
}

export function Stat({
  label,
  value,
  unit,
  hint,
}: {
  label: string
  value: ReactNode
  unit?: string
  hint?: ReactNode
}) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value numeric">
        {value}
        {unit && <span className="stat__unit">{unit}</span>}
      </div>
      {hint && <div className="stat__hint">{hint}</div>}
    </div>
  )
}

export type BadgeTone = 'open' | 'filling' | 'full' | 'empty' | 'danger' | 'warning' | 'muted'

/**
 * Une pastille de statut : pilule teintée, texte à pleine couleur.
 *
 * La puce ronde a disparu. Elle était là pour que le statut ne repose pas sur
 * la seule couleur — une bonne intention, mais un point de la même couleur
 * que le texte n'ajoute aucune information à quelqu'un qui distingue mal les
 * teintes. C'est le libellé qui fait ce travail, et il est toujours là.
 */
export function Badge({ tone = 'muted', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>
}

/**
 * La jauge de remplissage d'un créneau.
 *
 * Le seuil de couleur dit quelque chose d'opérationnel plutôt qu'esthétique :
 * en dessous de la moitié il reste de la marge, au-dessus de 85 % le créneau
 * est en passe de se fermer et c'est le moment d'en ouvrir un autre.
 */
export function Gauge({ sold, capacity }: { sold: number; capacity: number }) {
  const ratio = capacity > 0 ? Math.min(1, sold / capacity) : 0
  const modifier = ratio >= 0.85 ? '' : ratio >= 0.5 ? ' gauge__fill--filling' : ' gauge__fill--open'
  return (
    <div className="gauge">
      <div className={`gauge__fill${modifier}`} style={{ width: `${ratio * 100}%` }} />
    </div>
  )
}

export function Spinner() {
  return <div className="spinner" />
}

export function LoadingBlock() {
  const { t } = useI18n()
  return (
    <div className="empty">
      <div className="row" style={{ justifyContent: 'center' }}>
        <Spinner />
        <span>{t.app.loading}</span>
      </div>
    </div>
  )
}

/**
 * Un écran vide qui explique et propose.
 *
 * Une ligne grise au milieu d'une carte laisse la personne se demander si
 * l'outil a échoué, si elle a mal filtré, ou s'il n'y a vraiment rien. Un état
 * vide utile dit les trois choses qu'il faut : ce qui manque, pourquoi ça
 * compte, et le geste qui le règle.
 *
 * `mark` et `action` restent optionnels : une liste filtrée sans résultat n'a
 * pas besoin d'un bouton, elle a besoin qu'on lui dise que le filtre est en
 * cause.
 */
export function EmptyState({
  children,
  mark,
  hint,
  action,
}: {
  children: ReactNode
  mark?: ReactNode
  hint?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="empty">
      {mark && <div className="empty__mark">{mark}</div>}
      <div className="empty__title">{children}</div>
      {hint && <div className="empty__hint">{hint}</div>}
      {action && <div className="empty__action">{action}</div>}
    </div>
  )
}

/**
 * Ce qu'on affiche quand une lecture a échoué.
 *
 * Un refus de permission doit se dire, pas se déguiser en liste vide : « aucun
 * contact » sur un 403 fait croire à un gérant que son fichier client a
 * disparu, ce qui est la pire chose qu'un outil puisse laisser croire. La
 * distinction coûte une condition et évite un appel au support.
 */
export function QueryError({ error }: { error: { status: number; code: string } }) {
  const { t } = useI18n()
  return (
    <EmptyState>
      {error.status === 403 ? (
        <span style={{ color: 'var(--state-warning)' }}>{t.app.forbidden}</span>
      ) : (
        t.app.error
      )}
    </EmptyState>
  )
}

/**
 * Le chargement, puis l'erreur, d'un panneau qui occupe la page entière.
 *
 * `LoadingBlock` et `QueryError` sont faits pour vivre DANS une carte : rendus
 * nus sur la page, ils n'ont pas de fond, et un écran qui charge se lit alors
 * comme un écran vide — exactement ce que le reste de l'application s'interdit.
 * Le panneau d'exploitation, qui met deux secondes à répondre parce qu'il
 * interroge le parc, s'affichait blanc pendant ce temps.
 */
export function PanelLoading() {
  return (
    <div className="card">
      <LoadingBlock />
    </div>
  )
}

export function PanelError({ error }: { error: { status: number; code: string } }) {
  return (
    <div className="card">
      <QueryError error={error} />
    </div>
  )
}

/**
 * Le tiroir latéral. Xola ouvre les détails d'un créneau comme ça plutôt qu'en
 * changeant de page, et c'est le bon choix : on consulte un roster sans perdre
 * la grille de la journée derrière.
 */
/**
 * Une confirmation pour un geste qu'on ne rattrape pas.
 *
 * Le produit n'en avait aucune, et il commençait à en avoir besoin à plusieurs
 * endroits — effacer une décharge signée, révoquer une clé, retirer un groupe
 * d'appareils. `window.confirm` aurait fait l'affaire techniquement et rien
 * d'autre : il ne peut pas NOMMER ce qui va disparaître, et c'est justement le
 * seul contenu utile d'une confirmation. « Êtes-vous sûr ? » ne fait que
 * demander de cliquer une seconde fois.
 *
 * Le bouton de confirmation porte le VERBE du geste — « Supprimer », pas
 * « OK » — parce qu'on lit les boutons avant de lire le texte, et qu'un « OK »
 * sous une question négative se coche à l'envers une fois sur trois.
 */
export function Confirm({
  title,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string
  body: ReactNode
  confirmLabel: string
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useI18n()

  /* Échap annule. Même règle que le tiroir : un dialogue qu'on ne peut fermer
     qu'à la souris est une impasse au clavier — et ici l'issue par défaut doit
     être celle qui ne détruit rien. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <Portal>
      <div className="drawer-backdrop" onClick={onCancel} />
      <div className="confirm" role="alertdialog" aria-modal="true" aria-label={title}>
        <h2 className="confirm__title">{title}</h2>
        <div className="confirm__body">{body}</div>
        <div className="confirm__actions">
          <button className="btn" onClick={onCancel} disabled={busy}>
            {t.app.cancel}
          </button>
          <button className="btn btn--danger" onClick={onConfirm} disabled={busy}>
            {busy && <Spinner />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </Portal>
  )
}

export function Drawer({
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide,
  aside,
}: {
  title: ReactNode
  subtitle?: ReactNode
  /*
   * Un FAIT sur ce que le tiroir montre, posé en haut à droite — la fraîcheur
   * d'une lecture, par exemple. Il vit dans l'en-tête et non dans le corps
   * parce qu'il porte sur TOUT le contenu : le mettre dedans le ferait
   * ressembler à la première ligne d'une liste.
   */
  aside?: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  /*
   * UN TIROIR PLUS LARGE, pour un contenu qui se met en colonnes.
   *
   * C'est une option et pas la règle : 660 px est la bonne largeur pour lire
   * une fiche — une ligne de texte plus large se lit MOINS bien. Les réglages
   * de casque ne sont pas du texte, ce sont douze contrôles indépendants, et
   * la largeur y achète une colonne de plus donc un écran plus court.
   */
  wide?: boolean
}) {
  const { t } = useI18n()

  // Échap ferme. Un tiroir qu'on ne peut fermer qu'à la souris est une
  // impasse pour qui travaille au clavier au comptoir.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <Portal>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className={wide ? 'drawer drawer--wide' : 'drawer'} role="dialog" aria-modal="true" aria-label={String(title)}>
        <header className="drawer__header">
          <div className="row row--between">
            <div>
              <h2 style={{ fontSize: 'var(--fs-lg)', letterSpacing: '0.045em' }}>{title}</h2>
              {subtitle && (
                <div className="faint" style={{ fontSize: 'var(--fs-sm)', marginTop: 4 }}>
                  {subtitle}
                </div>
              )}
            </div>
            <div className="row" style={{ gap: 12 }}>
              {aside}
              <button className="btn btn--ghost btn--sm" onClick={onClose} aria-label={t.app.close}>
                <IconClose size={15} />
              </button>
            </div>
          </div>
        </header>
        <div className="drawer__body">{children}</div>
        {footer && <footer className="drawer__footer">{footer}</footer>}
      </aside>
    </Portal>
  )
}

/**
 * LA MODALE : une tâche qui remplace ce qu'on faisait.
 *
 * Même famille que `Drawer` — `<Portal>`, voile, Échap, `aria-modal` — et la
 * duplication s'arrête là où elle compte : un tiroir accompagne ce qu'on
 * regarde derrière, une modale n'a rien derrière qui aide. Inviter quelqu'un
 * est le second cas, et c'était rendu en carte EN BAS de la liste d'équipe :
 * le formulaire apparaissait hors du champ de vision, sous une liste qui peut
 * faire trois écrans, et rien ne disait qu'il s'était ouvert.
 *
 * Le clic sur le VOILE ferme, pas le clic sur la modale — d'où le `stopPropagation`.
 * Sans lui, cliquer dans le champ « Nom » refermerait le formulaire qu'on
 * vient d'ouvrir, ce qui se lit comme une panne.
 */
export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide = false,
  medium = false,
  side = false,
}: {
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  /** Pour une modale qui montre un TABLEAU (un plan d'ajout, un import). */
  wide?: boolean
  /** Entre les deux : un formulaire court qui a besoin d'une ligne entière. */
  medium?: boolean
  /**
   * À GAUCHE, sous un voile léger (Quentin, 14/10) : ouverte depuis le tiroir
   * d'un casque, la modale centrée recouvrait la vue en direct. Collée à
   * gauche, sans flou, elle laisse le tiroir lisible à droite.
   */
  side?: boolean
}) {
  const { t } = useI18n()

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <Portal>
      <div className={side ? 'modal-backdrop modal-backdrop--side' : 'modal-backdrop'} onClick={onClose}>
        <div
          className={wide ? 'modal modal--wide' : medium && !side ? 'modal modal--medium' : 'modal'}
          role="dialog"
          aria-modal="true"
          aria-label={String(title)}
          onClick={(event) => event.stopPropagation()}
        >
          <header className="modal__header">
            <div className="row row--between">
              <div>
                <h2 style={{ fontSize: 'var(--fs-lg)', letterSpacing: '0.045em' }}>{title}</h2>
                {subtitle && (
                  <div className="faint" style={{ fontSize: 'var(--fs-sm)', marginTop: 4 }}>
                    {subtitle}
                  </div>
                )}
              </div>
              <button className="btn btn--ghost btn--sm" onClick={onClose} aria-label={t.app.close}>
                <IconClose size={15} />
              </button>
            </div>
          </header>
          <div className="modal__body">{children}</div>
          {footer && <footer className="modal__footer">{footer}</footer>}
        </div>
      </div>
    </Portal>
  )
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: ReactNode
  children: ReactNode
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  )
}

/**
 * Un champ dont le contenu N'EST PAS un contrôle unique.
 *
 * `Field` est un `<label>`, et un label transfère le clic à son premier
 * contrôle. Y nicher un groupe de boutons — un sélecteur segmenté, un choix de
 * thème — fait qu'un clic sur le libellé ou sur le texte d'aide active le
 * PREMIER bouton du groupe : cliquer « Langue de l'interface » repassait
 * l'interface en français.
 *
 * Le piège est déjà arrivé sur le sélecteur de poste de l'équipe, où cliquer à
 * côté de « Gérance » posait « Borne de scan ». Ce composant existe pour qu'il
 * n'y ait plus à s'en souvenir : même apparence, `div` au lieu de `label`.
 */
export function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string
  hint?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </div>
  )
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  /*
   * `Icon` est FACULTATIVE, et doit le rester : la plupart des segmentés du
   * produit opposent deux mots — « Jour / Semaine », « Lieu / Intégrations » —
   * où un dessin n'ajouterait rien qu'une tache. Elle sert là où les segments
   * sont nombreux et désignent des OBJETS différents, comme le parc.
   */
  options: Array<{ value: T; label: string; Icon?: (p: { size?: number }) => React.ReactElement }>
  onChange: (value: T) => void
}) {
  return (
    <div className="segmented" role="group">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.Icon && <option.Icon size={14} />}
          {option.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Le champ de recherche, icône comprise.
 *
 * Il existait deux fois — Réservations et Clients — en positionnement absolu
 * recopié à l'identique, loupe comprise. C'est le contrôle le plus visible de
 * la barre d'outils de Xola (« Name, email, phone, tag or ID ») et il n'a pas
 * à être réinventé à chaque écran qui cherche quelque chose.
 */
export function SearchField({
  value,
  onChange,
  placeholder,
  autoFocus,
  label,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  autoFocus?: boolean
  /** Le nom du champ pour un lecteur d'écran, quand l'exemple du placeholder ne le dit pas. */
  label?: string
}) {
  return (
    <div className="searchfield">
      <span className="searchfield__icon">
        <IconSearch size={15} />
      </span>
      <input
        className="input searchfield__input"
        placeholder={placeholder}
        aria-label={label ?? placeholder}
        value={value}
        autoFocus={autoFocus}
        onChange={(event) => onChange(event.target.value)}
      />
      {/* La croix n'apparaît qu'une fois qu'il y a quelque chose à effacer :
          un bouton d'effacement permanent sur un champ vide est du bruit, et
          vider au clavier demande de sélectionner puis supprimer. */}
      {value && (
        <button
          className="searchfield__clear"
          onClick={() => onChange('')}
          aria-label="×"
          type="button"
        >
          <IconClose size={13} />
        </button>
      )}
    </div>
  )
}

/**
 * La pastille d'un filtre RETENU.
 *
 * Elle nomme le champ autant que la valeur — « Statut : confirmée » et non
 * « confirmée » — parce qu'une pastille isolée ne dit pas ce qu'elle exclut.
 * C'est l'emprunt le plus fonctionnel fait à Xola : un filtre invisible est la
 * première cause de « il n'y a rien aujourd'hui » alors qu'il y a trente
 * lignes.
 */
export function FilterChip({
  label,
  value,
  onClear,
}: {
  label: string
  value: string
  onClear: () => void
}) {
  return (
    <span className="chip">
      <span className="chip__label">{label}</span>
      {value}
      <button className="chip__x" onClick={onClear} aria-label="×" type="button">
        <IconClose size={11} />
      </button>
    </span>
  )
}

/**
 * L'interrupteur d'un état à deux positions.
 *
 * Il remplace les boutons « Activer » / « Désactiver » des parcours, qui
 * posaient deux problèmes : quatre parcours désactivés donnaient quatre
 * boutons VERTS sur un écran qui n'en admet qu'un, et le libellé décrivait
 * l'action alors que la colonne d'à côté décrivait l'état — on lisait
 * « Activer » sur une ligne active et l'hésitation durait une seconde à
 * chaque fois.
 *
 * Un interrupteur montre l'ÉTAT et se change d'un clic ; il n'a pas à choisir
 * entre les deux.
 */
export function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      disabled={disabled}
      className={checked ? 'switch switch--on' : 'switch'}
      onClick={() => onChange(!checked)}
    >
      <span className="switch__knob" />
    </button>
  )
}

/**
 * L'initiale d'une personne, sur une teinte dérivée de son nom.
 *
 * Xola met une photo à côté de chaque nom — de guide, de client — et c'est ce
 * qui rend leurs listes reconnaissables plutôt que lisibles. On n'a pas de
 * photos de clients et on n'en aura pas : la teinte dérivée du nom donne le
 * même bénéfice pour rien, et la même personne garde la même couleur d'un
 * écran à l'autre.
 *
 * C'est la mécanique de `Thumb` pour les expériences, appliquée aux gens. Elle
 * n'est pas partagée avec elle parce que les deux divergent sur l'essentiel :
 * une vignette d'expérience a une VRAIE image quand elle existe, un contact
 * n'en a jamais.
 */
export function Avatar({
  name,
  email,
  size = 30,
}: {
  name?: string | null
  email?: string | null
  size?: number
}) {
  const label = (name || email || '?').trim()

  /* Somme de codes de caractères : il faut seulement que la même chaîne rende
     toujours la même teinte, pas une fonction de hachage sérieuse. */
  let sum = 0
  for (let index = 0; index < label.length; index += 1) {
    sum = (sum * 31 + label.charCodeAt(index)) % 360
  }

  /*
   * Les initiales des deux premiers MOTS.
   *
   * « Olivier Lavoie-Desjardins » donne « OL », pas « Ol » : deux lettres de
   * mots différents distinguent, deux lettres du même mot font des pastilles
   * identiques dès que deux clients partagent un prénom.
   */
  const initials =
    label
      .split(/[\s·—–_.@-]+/)
      .filter((word) => /[\p{L}\p{N}]/u.test(word))
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase() || label.slice(0, 2).toUpperCase()

  return (
    <span
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.4),
        background: `oklch(0.42 0.07 ${sum})`,
        color: `oklch(0.93 0.05 ${sum})`,
      }}
      aria-hidden="true"
    >
      {initials}
    </span>
  )
}

/**
 * Un en-tête de colonne TRIABLE.
 *
 * Nos tableaux se triaient côté serveur ou pas du tout, et rien dans l'en-tête
 * ne le disait — donc personne n'essayait. Xola met un chevron sur chaque
 * colonne triable et le remplit sur la colonne active ; c'est deux pixels
 * d'encre pour une fonction qu'on croyait absente.
 *
 * Le tri est CLIENT : les deux écrans concernés chargent cent lignes au plus,
 * et refaire un aller-retour serveur pour réordonner cent lignes déjà en
 * mémoire ajouterait une latence visible à un geste qu'on répète.
 */
export function SortHeader<K extends string>({
  column,
  active,
  direction,
  onSort,
  numeric,
  children,
}: {
  column: K
  active: K | null
  direction: 'asc' | 'desc'
  onSort: (column: K) => void
  numeric?: boolean
  children: ReactNode
}) {
  const isActive = active === column
  return (
    <th className={numeric ? 'numeric' : undefined}>
      <button
        type="button"
        className={isActive ? 'sorth sorth--active' : 'sorth'}
        onClick={() => onSort(column)}
        style={numeric ? { justifyContent: 'flex-end' } : undefined}
      >
        {children}
        <span className="sorth__arrow">
          {isActive ? (
            direction === 'asc' ? (
              <IconChevronUp size={12} />
            ) : (
              <IconChevronDown size={12} />
            )
          ) : (
            <IconChevronDown size={12} />
          )}
        </span>
      </button>
    </th>
  )
}

/**
 * L'état de tri d'un tableau, et le comparateur qui va avec.
 *
 * Il vit ici parce que deux écrans en ont besoin et qu'un tri recopié dérive :
 * l'un finirait par trier les `null` en tête et l'autre en queue, et personne
 * ne saurait lequel a raison. Les valeurs absentes vont TOUJOURS à la fin,
 * dans les deux sens — « jamais venu » n'est pas « venu il y a très
 * longtemps », et le remonter en tête d'un tri décroissant serait faux.
 */
export function useSort<K extends string>(initial: K, initialDirection: 'asc' | 'desc' = 'asc') {
  const [column, setColumn] = useState<K>(initial)
  const [direction, setDirection] = useState<'asc' | 'desc'>(initialDirection)

  function onSort(next: K) {
    if (next === column) {
      setDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
    } else {
      setColumn(next)
      /* Une nouvelle colonne repart en croissant : on vient de la choisir, on
         n'a pas encore d'opinion sur le sens. */
      setDirection('asc')
    }
  }

  function compare(a: unknown, b: unknown): number {
    const missing = (v: unknown) => v === null || v === undefined || v === ''
    if (missing(a) && missing(b)) return 0
    if (missing(a)) return 1
    if (missing(b)) return -1

    const sign = direction === 'asc' ? 1 : -1
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * sign
    return String(a).localeCompare(String(b), undefined, { numeric: true }) * sign
  }

  return { column, direction, onSort, compare }
}

/**
 * Sort son contenu de l'arbre du DOM pour le poser sur `document.body`.
 *
 * Indispensable pour les tiroirs et les modales, et pas seulement par
 * élégance : **un ancêtre qui porte un `transform` devient le bloc conteneur
 * de ses descendants `position: fixed`**. Les animations d'apparition laissent
 * précisément un `translateY(0)` résiduel sur le conteneur de page — un
 * transform à zéro reste un transform — et le tiroir, au lieu d'occuper la
 * hauteur de la fenêtre, se retrouvait enfermé dans la carte qui l'avait
 * ouvert. Le portail rend le tiroir insensible à ce que font ses ancêtres.
 */
/**
 * UN « ? » qui explique, et la bulle qui va avec.
 *
 * Le produit n'avait que l'attribut `title` du navigateur — quarante-neuf
 * emplois. Il convient à un mot (« retirer de ce créneau ») et pas à une
 * explication : pas de mise en forme, une seconde d'attente, et surtout RIEN
 * au doigt, alors que la moitié des écrans se lisent sur une tablette.
 *
 * Trois choix qui comptent :
 *
 * - Elle s'ouvre au CLIC autant qu'au survol. Un « ? » qui ne répond qu'à la
 *   souris est un « ? » mort sur un écran tactile, et c'est précisément le
 *   lecteur qui a le plus besoin d'aide.
 * - Elle passe par `<Portal>`, comme les tiroirs et pour la même raison : un
 *   ancêtre en `overflow` la découperait, et les cartes de ce produit passent
 *   en `overflow-x: auto` sur mobile. Sa position vient donc du rectangle du
 *   bouton, pas du flux.
 * - Elle ferme à l'ÉCHAP et au clic ailleurs. Une bulle qu'on ne sait pas
 *   fermer se fait fermer en rechargeant la page.
 */
export function Explain({ children, label }: { children: ReactNode; label?: string }) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)
  const anchor = useRef<HTMLButtonElement | null>(null)

  const place = useCallback(() => {
    const box = anchor.current?.getBoundingClientRect()
    if (!box) return
    /* Bornée à la fenêtre : près du bord droit, une bulle de trois cents
       pixels sortirait de l'écran et ne se lirait qu'à moitié. */
    const width = 320
    setAt({
      top: box.bottom + 8,
      left: Math.max(12, Math.min(box.left - width / 2 + box.width / 2, window.innerWidth - width - 12)),
    })
  }, [])

  useEffect(() => {
    if (!at) return
    const close = () => setAt(null)
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && close()
    /* `capture` pour attraper le clic avant qu'un gestionnaire de la page ne
       l'arrête — sinon la bulle survit à un clic sur une ligne cliquable. */
    window.addEventListener('click', close, true)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('click', close, true)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
      window.removeEventListener('keydown', onKey)
    }
  }, [at])

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="explain"
        aria-label={label ?? '?'}
        aria-expanded={at !== null}
        onClick={(event) => {
          /*
           * Une vraie bascule. Le clic n'ouvrait que : l'écouteur global en
           * phase de CAPTURE ferme d'abord — il passe avant le bouton —
           * puis ce gestionnaire rouvrait, et la bulle refusait de se
           * refermer sous le doigt qui venait de l'ouvrir.
           */
          event.stopPropagation()
          if (at) setAt(null)
          else place()
        }}
        onMouseEnter={place}
        onMouseLeave={() => setAt(null)}
        onFocus={place}
        onBlur={() => setAt(null)}
      >
        ?
      </button>
      {at && (
        <Portal>
          <div className="explain__bubble" style={{ top: at.top, left: at.left }} role="tooltip">
            {children}
          </div>
        </Portal>
      )}
    </>
  )
}

/**
 * COPIER, et le dire.
 *
 * C'est le geste le plus silencieux du produit : le presse-papier n'a aucune
 * manifestation visible, donc on reclique — et sur un lien de borne, une clé
 * d'ingestion ou l'adresse d'un écran, on ne sait jamais si on tient l'ancien
 * ou le nouveau. Quatre endroits le faisaient, aucun ne répondait.
 *
 * Un hook et non une fonction libre : il lui faut les chaînes traduites, et
 * les deviner depuis un module hors composant est précisément le piège noté
 * dans CLAUDE.md (« ne jamais deviner la langue depuis une chaîne
 * traduite »).
 */
export function useCopy(): (value: string) => void {
  const { t } = useI18n()
  return (value: string) => {
    /* `navigator.clipboard` est absent hors contexte sécurisé — un accès en
       HTTP sur le LAN, ce qui est exactement le cas d'une tablette d'accueil.
       Le refus doit se voir, sinon on croit avoir copié. */
    void Promise.resolve(navigator.clipboard?.writeText(value))
      .then(() => toast.ok(t.ob.copied))
      .catch((caught: unknown) => toast.bad(t.toast.failed, caught))
  }
}

/**
 * SA PROPRE photo de profil, chargée avec le jeton.
 *
 * `<img src="/api/auth/avatar/…">` n'emportait aucun en-tête, donc la route
 * devait être publique — et n'importe qui tenant l'identifiant d'un membre du
 * personnel obtenait sa photo. On passe par `fetch`, qui porte le jeton, et on
 * rend une URL d'objet.
 *
 * L'URL est RÉVOQUÉE au démontage et à chaque changement. Sans ça, chaque
 * téléversement de photo laisse un blob vivant pour la durée de l'onglet —
 * une fuite lente que rien ne signale, sur un poste de comptoir qu'on ne
 * ferme pas de la journée.
 */
export function useSelfAvatar(avatarUrl: string | null | undefined): string | null {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!avatarUrl) {
      setObjectUrl(null)
      return
    }

    let revoked = false
    let current: string | null = null

    void fetch('/api/auth/me/avatar', { headers: authHeaders() })
      .then((response) => (response.ok ? response.blob() : null))
      .then((blob) => {
        if (!blob) return
        current = URL.createObjectURL(blob)
        /* L'effet a pu être nettoyé pendant le transfert — changement de
           photo, navigation. On révoque tout de suite plutôt que de poser un
           état sur un composant démonté. */
        if (revoked) URL.revokeObjectURL(current)
        else setObjectUrl(current)
      })
      .catch(() => undefined)

    return () => {
      revoked = true
      if (current) URL.revokeObjectURL(current)
    }
  }, [avatarUrl])

  return objectUrl
}

export function Portal({ children }: { children: ReactNode }) {
  return createPortal(children, document.body)
}


/*
 * UN BLOC DE PAGE, toujours le même gabarit : un titre (et son icône), une
 * phrase qui dit à quoi il sert, puis le contenu — avec ses marges. Les
 * blocs de la console avaient chacun le leur, dont plusieurs collés au bord
 * de la carte ; c'est ce qui faisait patchwork.
 */
export function Section({
  title,
  icon,
  hint,
  aside,
  children,
}: {
  title: ReactNode
  icon?: ReactNode
  hint?: ReactNode
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="card">
      <header className="card__header">
        <span className="card__title section__title">
          {icon}
          {title}
        </span>
        <span style={{ flex: 1 }} />
        {aside}
      </header>
      <div className="card__body stack">
        {hint && <p className="section__hint">{hint}</p>}
        {children}
      </div>
    </section>
  )
}

/*
 * CHOISIR UN FICHIER, sans le bouton gris du navigateur.
 *
 * « Choose File · No file chosen » ne suit ni la langue ni le thème de
 * l'écran. Le champ natif reste — c'est lui qui ouvre le sélecteur, au
 * clavier comme au doigt — mais caché derrière un bouton et le nom choisi.
 */
export function FilePick({
  accept,
  file,
  onPick,
  label,
  empty,
}: {
  accept: string
  file: File | null
  onPick: (file: File | null) => void
  label: string
  empty: string
}) {
  return (
    <label className="filepick">
      <input type="file" accept={accept} onChange={(event) => onPick(event.target.files?.[0] ?? null)} />
      <span className="btn btn--sm">{label}</span>
      <span className={file ? 'filepick__name' : 'filepick__name faint'}>{file ? file.name : empty}</span>
    </label>
  )
}
